import React from "react";
import {
  FileText, Download, Coins, Users,
  Archive, Sparkles, Rocket, Trophy, Landmark, Zap, Megaphone, FlaskConical, BadgeCheck
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { ContractBadge, CONTRACT_ADDRESS } from "@/components/ContractBadge";

// --------------------------------------------------------------
// EDIT THESE when updated:
const PDF_URL  = "/paul-whitepaper.pdf";
const PDF_META = {
  title:     "$PAUL — Paul The Alien",
  subtitle:  "Official Whitepaper",
  version:   "Version 1.0",
  date:      "April 2026",
  pages:     3,
  website:   "https://paulthealien.com/",
};
const TOTAL_SUPPLY = "1,000,000,000";
const ALLOCATIONS = [
  {
    label: "Public / Fair Launch",
    pct:   90,
    color: "#2EC87A",
    icon:  Users,
    note:  "Open market. No presale. No insiders. Straight to the people.",
    bullets: null,
  },
  {
    label: "Dev Wallet",
    pct:   10,
    color: "#7A3EFF",
    icon:  Coins,
    note:  "Used for:",
    bullets: [
      "Community rewards",
      "Marketing",
      "Exchange listings",
      "Long-term token growth",
    ],
  },
];
// --------------------------------------------------------------

/** Bulletproof download — forces the browser to actually save the PDF. */
const downloadWhitepaper = (e) => {
  if (e) e.preventDefault();
  try {
    const a = document.createElement("a");
    a.href = PDF_URL;
    a.download = "PAUL-Whitepaper-v1.pdf";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Whitepaper downloaded");
  } catch (err) {
    console.error(err);
    window.open(PDF_URL, "_blank", "noopener,noreferrer");
  }
};

/** Section heading with a matching number tab like a chapter file. */
const WPHeading = ({ num, title, kicker }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3">
      <span className="font-mono text-[10px] tracking-[0.3em] text-green uppercase">
        {kicker || `Chapter ${num}`}
      </span>
      <span className="h-px flex-1 bg-white/20" />
      <span className="font-mono text-[10px] tracking-[0.3em] text-white/50">
        {String(num).padStart(2, "0")} / 11
      </span>
    </div>
    <h3 className="font-neon text-3xl md:text-4xl text-white glow-white mt-3">{title}</h3>
  </div>
);

const BulletList = ({ items, iconTint = "var(--p-green)" }) => (
  <ul className="mt-3 space-y-2">
    {items.map((t) => (
      <li key={t} className="flex items-start gap-3 font-type text-white/90 text-base leading-relaxed">
        <span
          className="mt-2 inline-block w-1.5 h-1.5 flex-shrink-0 rounded-full"
          style={{ background: iconTint }}
        />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Para = ({ children }) => (
  <p className="font-type text-white/90 text-base md:text-lg leading-relaxed">{children}</p>
);

// ============================================================================

export default function TokenomicsPage() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="tokenomics-page">
      <Toaster position="top-right" richColors />

      {/* PAGE HEADER */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="font-mono text-xs text-green tracking-[0.3em]">MODULE / T-01</span>
        <span className="font-mono text-xs text-white/70">tokenomics · whitepaper</span>
      </div>
      <h1 className="font-neon text-5xl md:text-7xl text-white glow-white mt-3">
        <span className="font-mono font-extrabold mr-2">$</span>TOKENOMICS
      </h1>
      <p className="font-type text-white text-lg mt-2 max-w-2xl">
        The paperwork on Paul. Supply, distribution, and the full whitepaper — straight from the
        alien's folder to yours.
      </p>

      {/* SUMMARY CARDS */}
      <div className="grid md:grid-cols-3 gap-5 mt-12" data-testid="token-summary">
        <div className="paper-card px-6 py-5 no-tape">
          <div className="font-mono text-[10px] tracking-[0.25em] text-ink/70">TICKER</div>
          <div className="font-neon text-3xl text-ink mt-2">$PAUL</div>
          <div className="font-mono text-[11px] text-ink/60 mt-1 tracking-[0.2em]">SOLANA · SPL</div>
        </div>

        <div className="paper-card px-6 py-5 no-tape">
          <div className="font-mono text-[10px] tracking-[0.25em] text-ink/70">TOTAL SUPPLY</div>
          <div className="font-neon text-3xl text-ink mt-2">{TOTAL_SUPPLY}</div>
          <div className="font-mono text-[11px] text-ink/60 mt-1 tracking-[0.2em]">FIXED · NO MINT</div>
        </div>

        <div className="paper-card px-6 py-5 no-tape">
          <div className="font-mono text-[10px] tracking-[0.25em] text-ink/70">CONTRACT</div>
          <div className="mt-3">
            <ContractBadge size="md" dataTestid="tokenomics-contract-address" />
          </div>
          <div className="font-mono text-[11px] text-ink/60 mt-3 tracking-[0.2em]">
            {CONTRACT_ADDRESS === "TBA" ? "ADDRESS DROPS AT LAUNCH" : "CLICK TO COPY · SOLANA · SPL"}
          </div>
        </div>
      </div>

      {/* ALLOCATION */}
      <div className="mt-14" data-testid="allocation-section">
        <div className="font-mono text-xs tracking-[0.3em] text-green">ALLOCATION</div>
        <h2 className="font-neon text-3xl md:text-4xl text-white mt-2 glow-white">How the pie gets sliced.</h2>

        <div className="mt-6 h-4 w-full rounded-full overflow-hidden flex border border-white" data-testid="allocation-bar">
          {ALLOCATIONS.map((a) => (
            <div key={a.label} style={{ width: `${a.pct}%`, background: a.color }} title={`${a.label} · ${a.pct}%`} />
          ))}
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="allocation-list">
          {ALLOCATIONS.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={a.label} className="paper-card no-tape px-6 py-5" data-testid={`allocation-${i}`}>
                <div className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-sm flex-shrink-0"
                    style={{ background: a.color, color: "#0a0a0a" }}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-type text-ink text-xl">{a.label}</div>
                      <div className="font-mono text-ink text-2xl font-bold">{a.pct}%</div>
                    </div>
                    <div className="font-type text-ink/80 text-sm mt-1">{a.note}</div>
                    {a.bullets && (
                      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5">
                        {a.bullets.map((t) => (
                          <li key={t} className="flex items-start gap-2 font-type text-ink text-sm">
                            <span
                              className="mt-2 inline-block w-1.5 h-1.5 flex-shrink-0 rounded-full"
                              style={{ background: a.color }}
                            />
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* =========================== WHITEPAPER =========================== */}
      <div className="mt-24" data-testid="whitepaper-section">
        {/* Title block */}
        <div className="text-center">
          <span className="redacted-stamp inline-block" style={{ transform: "rotate(-3deg)" }}>
            DECLASSIFIED
          </span>
          <h2 className="font-neon text-4xl md:text-6xl text-white glow-white mt-6">
            OFFICIAL WHITEPAPER
          </h2>
          <div className="font-type text-white/85 text-xl mt-2">{PDF_META.title}</div>
          <div className="font-mono text-[11px] tracking-[0.3em] text-white/70 mt-3 uppercase">
            {PDF_META.version} · {PDF_META.date} · <a href={PDF_META.website} className="underline hover:text-green">paulthealien.com</a>
          </div>
        </div>

        {/* Chapters */}
        <article className="mt-16 space-y-20">
          {/* 01 Introduction */}
          <section data-testid="wp-introduction">
            <WPHeading num={1} title="Introduction" />
            <Para>
              $PAUL is a community-driven meme token powered by the iconic grey alien Paul — the
              lovable extraterrestrial who's been here since '47.
            </Para>
            <Para>
              As governments worldwide begin to lift the veil on Unidentified Aerial Phenomena (UAP)
              and extraterrestrial contact, humanity's fascination with alien life has never been
              stronger.
            </Para>
            <Para>
              $PAUL exists to become the internet's ultimate hub for alien disclosure, UFO lore and
              extraterrestrial conversation — all with Paul as the friendly face leading the charge
              through memes, laughs and genuine curiosity.
            </Para>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              {[
                { icon: Users,       t: "Community engagement" },
                { icon: Archive,     t: "Disclosure research" },
                { icon: Sparkles,    t: "Internet meme culture" },
              ].map(({ icon: Ic, t }) => (
                <div key={t} className="border border-white/25 bg-ink/40 px-4 py-3 flex items-center gap-3" style={{ borderRadius: 4 }}>
                  <Ic size={16} className="text-green" />
                  <span className="font-type text-white text-sm">{t}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 02 Vision */}
          <section data-testid="wp-vision">
            <WPHeading num={2} title="Vision" />
            <Para>
              The mission of $PAUL is to build the biggest and most entertaining online ecosystem for
              alien disclosure and extraterrestrial discussion, where Paul finally gets the homecoming
              he deserves.
            </Para>
            <BulletList
              items={[
                "Connect with a worldwide community of believers and skeptics alike",
                "Craft hilarious Paul-themed memes",
                "Explore declassified government files",
                "Join contests and giveaways",
              ]}
            />
          </section>

          {/* 03 Utility */}
          <section data-testid="wp-utility">
            <WPHeading num={3} title="Utility" />
            <div className="grid md:grid-cols-2 gap-5 mt-2">
              <div className="paper-card no-tape p-6" data-testid="wp-utility-vault">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm" style={{ background: "#2EC87A", color: "#0a0a0a" }}>
                    <FlaskConical size={18} />
                  </div>
                  <div className="font-type text-ink text-2xl">UAP Vault</div>
                </div>
                <p className="font-type text-ink/85 text-sm mt-3 leading-relaxed">
                  The platform serves as the definitive central archive for all things alien and
                  UAP/UFO-related, spotlighting Paul as the mascot of modern disclosure.
                </p>
                <ul className="mt-3 space-y-1.5 font-type text-ink text-sm">
                  {[
                    "Government document releases",
                    "Historical cases",
                    "Newly declassified information",
                    "Research archives",
                    "UAP/UFO incident reports",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="mt-2 inline-block w-1.5 h-1.5 flex-shrink-0 rounded-full bg-ink" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="paper-card no-tape p-6" data-testid="wp-utility-memelab">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-sm" style={{ background: "#7A3EFF", color: "#fff" }}>
                    <Sparkles size={18} />
                  </div>
                  <div className="font-type text-ink text-2xl">Meme Generator (MemeLab)</div>
                </div>
                <p className="font-type text-ink/85 text-sm mt-3 leading-relaxed">
                  Create and share custom Paul-themed memes straight from the platform — turning
                  disclosure into daily viral fun.
                </p>
                <ul className="mt-3 space-y-1.5 font-type text-ink text-sm">
                  {[
                    "Paul meme templates",
                    "Alien filters and effects",
                    "Community meme contests with real rewards",
                    "Download and share to your favourite social platform",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="mt-2 inline-block w-1.5 h-1.5 flex-shrink-0 rounded-full bg-ink" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 04 Tokenomics */}
          <section data-testid="wp-tokenomics">
            <WPHeading num={4} title="Tokenomics" />
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { k: "TOKEN NAME",    v: "Paul The Alien" },
                { k: "TICKER",        v: "$PAUL" },
                { k: "TOTAL SUPPLY",  v: "1,000,000,000" },
              ].map((row) => (
                <div key={row.k} className="border border-white/25 bg-ink/40 px-4 py-3" style={{ borderRadius: 4 }}>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-green">{row.k}</div>
                  <div className="font-type text-white text-lg mt-1">{row.v}</div>
                </div>
              ))}
            </div>
            <Para>
              <span className="font-mono text-green font-bold">10%</span> of the supply is held in a
              Dev Wallet to support the ecosystem long term.
            </Para>
            <div className="mt-4 border border-white/25 bg-ink/40 px-6 py-5" style={{ borderRadius: 4 }}>
              <div className="font-mono text-[10px] tracking-[0.25em] text-green mb-2 uppercase">Dev wallet — used for</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { icon: Trophy,   t: "Community rewards" },
                  { icon: Megaphone,t: "Marketing" },
                  { icon: Landmark, t: "Exchange listings" },
                  { icon: Rocket,   t: "Long-term token growth" },
                ].map(({ icon: Ic, t }) => (
                  <div key={t} className="flex items-center gap-3 font-type text-white text-base">
                    <Ic size={16} className="text-green flex-shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 05 Community Incentives */}
          <section data-testid="wp-community">
            <WPHeading num={5} title="Community Incentives" />
            <Para>
              Community is everything at $PAUL. Holders are rewarded for spreading the word and
              keeping the vibes high.
            </Para>
            <BulletList
              items={[
                "Community competitions",
                "Regular giveaways",
                "Meme contests",
                "Milestone rewards",
              ]}
            />
          </section>

          {/* 06 Project Reach */}
          <section data-testid="wp-reach">
            <WPHeading num={6} title="Project Reach" />
            <div className="grid sm:grid-cols-3 gap-3 mt-2">
              {["Jupiter listing", "CoinGecko listing", "CoinMarketCap listing"].map((t) => (
                <div key={t} className="border border-white/25 bg-ink/40 px-4 py-3 flex items-center gap-3" style={{ borderRadius: 4 }}>
                  <BadgeCheck size={16} className="text-green" />
                  <span className="font-type text-white text-base">{t}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 07 Why the narrative wins */}
          <section data-testid="wp-narrative">
            <WPHeading num={7} title="Why This Narrative Will Be A Winner" />
            <Para>
              Alien disclosure is no longer fringe — it's going mainstream. Donald Trump remains the
              only major world leader to publicly vow the release of classified UFO and alien files.
            </Para>
            <Para>
              As more documents and eyewitness accounts surface, public interest will explode for
              years to come. Paul — the relatable, sarcastic, pop-culture alien — perfectly captures
              the lighter, more approachable side of disclosure.
            </Para>
            <div className="mt-5 paper-card no-tape px-6 py-5 inline-block">
              <div className="font-type text-ink text-xl">Transitioning from conspiracy to reality.</div>
            </div>
          </section>

          {/* 08 Why $PAUL can go viral */}
          <section data-testid="wp-viral">
            <WPHeading num={8} title="Why $PAUL Can Go Viral" />
            <Para>
              Successful meme coins thrive at the crossroads of culture, story and community. $PAUL
              perfectly blends:
            </Para>
            <BulletList
              items={[
                "Endless viral social media potential",
                "High-stakes political disclosure narrative",
                "The universally beloved alien Paul from the big screen",
                "Timeless internet meme energy",
              ]}
            />
            <div className="mt-5 font-type italic text-xl text-green">
              It's disclosure with humor, heart and hopium.
            </div>
          </section>

          {/* 09 Roadmap */}
          <section data-testid="wp-roadmap">
            <WPHeading num={9} title="Roadmap" />
            <div className="grid md:grid-cols-2 gap-5 mt-2">
              {[
                {
                  num: 1, name: "Launch", status: "PHASE 1", color: "#2EC87A",
                  items: ["Token launch", "Website V1 launch", "Community formation", "Initial giveaways", "Meme generator release (MemeLab)", "Verify X"]
                },
                {
                  num: 2, name: "Utility Expansion", status: "PHASE 2", color: "#7A3EFF",
                  items: ["Community growth", "Token burn", "Updated disclosure news board"]
                },
                {
                  num: 3, name: "Marketing Expansion", status: "PHASE 3", color: "#F4CC07",
                  items: ["Expanded marketing campaigns"]
                },
                {
                  num: 4, name: "Project Growth", status: "PHASE 4", color: "#FF8A00",
                  items: ["Expanded disclosure database", "Crypto exchange listings"]
                },
              ].map((ph) => (
                <div key={ph.num} className="border border-white/25 bg-ink/40 p-5" style={{ borderRadius: 4 }} data-testid={`wp-phase-${ph.num}`}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center font-neon text-lg"
                      style={{ background: ph.color, color: "#0a0a0a", borderRadius: 4 }}
                    >
                      {ph.num}
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.25em] text-white/70">{ph.status}</div>
                      <div className="font-type text-white text-xl leading-tight">{ph.name}</div>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-1.5 font-type text-white/90 text-sm">
                    {ph.items.map((t) => (
                      <li key={t} className="flex items-start gap-2">
                        <span className="mt-2 inline-block w-1.5 h-1.5 flex-shrink-0 rounded-full" style={{ background: ph.color }} />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* 10 Conclusion */}
          <section data-testid="wp-conclusion">
            <WPHeading num={10} title="Conclusion" />
            <Para>
              The world is stepping into a bold new chapter of alien disclosure and extraterrestrial
              wonder.
            </Para>
            <Para>
              $PAUL is here to make that journey unforgettable — turning government files into memes,
              sightings into laughs and one lonely alien's quest into a global movement.
            </Para>
            <div className="mt-6 text-center">
              <div className="font-type text-green text-2xl italic">Paul The Alien — where disclosure meets the culture.</div>
              <div className="font-neon text-4xl md:text-6xl text-white glow-white mt-4">LET'S BRING PAUL HOME.</div>
              <div className="font-mono text-[11px] tracking-[0.3em] text-white/70 mt-4 uppercase">
                Join the movement · <a href={PDF_META.website} className="underline hover:text-green">paulthealien.com</a>
              </div>
              <div className="mt-4 font-type italic text-xl text-white">
                "$PAUL — I'm not a secret. I'm a leak."
              </div>
            </div>
          </section>

          {/* Big download CTA */}
          <section
            className="text-center px-6 py-10 border-2 border-white bg-ink/60 backdrop-blur-sm"
            style={{ borderRadius: 6, boxShadow: "6px 6px 0 0 rgba(0,0,0,0.55)" }}
            data-testid="whitepaper-download-strip"
          >
            <FileText size={28} className="text-green mx-auto" />
            <div className="font-type text-white text-2xl mt-3">Take the dossier with you.</div>
            <div className="font-mono text-[11px] tracking-[0.3em] text-white/70 mt-2 uppercase">
              PDF · {PDF_META.version} · {PDF_META.date}
            </div>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <button
                type="button"
                onClick={downloadWhitepaper}
                className="btn-paul green"
                data-testid="whitepaper-download-btn-main"
              >
                <Download size={14} /> DOWNLOAD WHITEPAPER
              </button>
            </div>
          </section>

          {/* 11 Disclaimer */}
          <section data-testid="wp-disclaimer">
            <WPHeading num={11} title="Disclaimer" kicker="Chapter 11 · Fine Print" />
            <div className="border border-white/20 bg-ink/40 px-6 py-5" style={{ borderRadius: 4 }}>
              <div className="inline-block px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase border-2 mb-4"
                   style={{ borderColor: "var(--p-red)", color: "var(--p-red)", background: "rgba(255,74,58,0.08)" }}>
                ⚠ Not Financial Advice
              </div>
              <Para>
                <span className="text-white">$PAUL is a memecoin. Vibes only. DYOR.</span>
              </Para>
              <p className="font-type text-white/75 text-sm leading-relaxed mt-4">
                The character "Paul" and all related imagery are from the 2011 comedy film <em>Paul</em>,
                directed by Greg Mottola and written by Simon Pegg &amp; Nick Frost. The voice and
                motion capture performance for Paul was provided by Seth Rogen.
              </p>
              <p className="font-type text-white/75 text-sm leading-relaxed mt-3">
                This website and the $PAUL memecoin project are fan-made / community-driven and are
                not affiliated with, endorsed by, or connected to the original filmmakers, Universal
                Pictures, Working Title Films, or any rights holders. The image is used here for
                parody and meme purposes only. All rights to the original character and film belong
                to their respective owners.
              </p>
              <p className="font-mono text-[11px] text-white/60 tracking-[0.2em] uppercase mt-3">
                No copyright infringement is intended.
              </p>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}
